import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

let ffmpegInstance: FFmpeg | null = null;
let loadingPromise: Promise<FFmpeg> | null = null;

const CORE_BASE_URL = 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd';

async function getFFmpeg(onLog?: (msg: string) => void): Promise<FFmpeg> {
    if (ffmpegInstance) return ffmpegInstance;
    if (loadingPromise) return loadingPromise;

    loadingPromise = (async () => {
        const ffmpeg = new FFmpeg();
        if (onLog) ffmpeg.on('log', ({ message }) => onLog(message));

        await ffmpeg.load({
            coreURL: await toBlobURL(`${CORE_BASE_URL}/ffmpeg-core.js`, 'text/javascript'),
            wasmURL: await toBlobURL(`${CORE_BASE_URL}/ffmpeg-core.wasm`, 'application/wasm'),
        });
        ffmpegInstance = ffmpeg;
        return ffmpeg;
    })();

    return loadingPromise;
}

export interface CompressOptions {
    onProgress?: (pct: number) => void;
    onPhase?: (phase: 'loading' | 'compressing' | 'finalizing') => void;
    maxHeight?: number;
    videoBitrate?: string;
    audioBitrate?: string;
}

export async function compressVideo(file: File, opts: CompressOptions = {}): Promise<Blob> {
    const {
        onProgress,
        onPhase,
        maxHeight = 720,
        videoBitrate = '1500k',
        audioBitrate = '96k',
    } = opts;

    onPhase?.('loading');
    const ffmpeg = await getFFmpeg();

    onPhase?.('compressing');
    const progressHandler = ({ progress }: { progress: number }) => {
        if (onProgress) onProgress(Math.min(100, Math.max(0, Math.round(progress * 100))));
    };
    ffmpeg.on('progress', progressHandler);

    const inputName = 'input' + getExtension(file.name);
    const outputName = 'output.mp4';

    try {
        await ffmpeg.writeFile(inputName, await fetchFile(file));

        await ffmpeg.exec([
            '-i', inputName,
            '-vf', `scale='min(iw,trunc(oh*iw/ih/2)*2)':'min(${maxHeight},trunc(ih/2)*2)'`,
            '-c:v', 'libx264',
            '-preset', 'veryfast',
            '-crf', '28',
            '-b:v', videoBitrate,
            '-maxrate', videoBitrate,
            '-bufsize', '3000k',
            '-c:a', 'aac',
            '-b:a', audioBitrate,
            '-movflags', '+faststart',
            outputName,
        ]);

        onPhase?.('finalizing');
        const data = await ffmpeg.readFile(outputName);
        const blob = new Blob([data as unknown as BlobPart], { type: 'video/mp4' });

        try { await ffmpeg.deleteFile(inputName); } catch {}
        try { await ffmpeg.deleteFile(outputName); } catch {}

        return blob;
    } finally {
        ffmpeg.off('progress', progressHandler);
    }
}

function getExtension(name: string): string {
    const i = name.lastIndexOf('.');
    if (i < 0) return '.mp4';
    const ext = name.substring(i).toLowerCase();
    if (/^\.(mp4|mov|webm|m4v|avi|mkv|ogg)$/.test(ext)) return ext;
    return '.mp4';
}

export function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
